import {
  Box,
  Typography,
  Divider,
  Button,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { DashboardFiltersDto } from "../../../types/dashboard";
import { useState, useEffect } from "react";

interface CEOFiltersProps {
  filters: DashboardFiltersDto;
  onFiltersChange: (filters: DashboardFiltersDto) => void;
}

export const CEOFilters = ({ filters, onFiltersChange }: CEOFiltersProps) => {
  const [tempFilters, setTempFilters] = useState<DashboardFiltersDto>(filters);

  useEffect(() => {
    setTempFilters(filters);
  }, [filters]);

  const hasFilters =
    tempFilters.startDate?.start !== undefined ||
    tempFilters.startDate?.end !== undefined ||
    tempFilters.endDate?.start !== undefined ||
    tempFilters.endDate?.end !== undefined ||
    tempFilters.billingMin !== undefined ||
    tempFilters.billingMax !== undefined ||
    tempFilters.revenueMin !== undefined ||
    tempFilters.revenueMax !== undefined ||
    tempFilters.audienceMin !== undefined ||
    tempFilters.audienceMax !== undefined ||
    tempFilters.eventScope !== undefined ||
    tempFilters.hasCompetitor !== undefined;

  const hasChanges = JSON.stringify(tempFilters) !== JSON.stringify(filters);

  const handleClearFilters = () => {
    const clearedFilters: DashboardFiltersDto = {
      includeExtraFields: true,
    };
    setTempFilters(clearedFilters);
    onFiltersChange(clearedFilters);
  };

  const handleMinMaxChange = (field: string, value: string) => {
    const numValue = value === "" ? undefined : Number(value);
    setTempFilters({
      ...tempFilters,
      [field]: numValue,
    });
  };

  const handleApplyFilters = () => {
    onFiltersChange(tempFilters);
  };

  return (
    <Box sx={{ width: "320px" }}>
      <Box p={3}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: 18 }}>
            Filtros
          </Typography>
          {hasFilters && (
            <Button
              size="small"
              variant="text"
              onClick={handleClearFilters}
              sx={{
                textTransform: "none",
                fontSize: 14,
                color: "text.secondary",
                "&:hover": {
                  backgroundColor: "transparent",
                  textDecoration: "underline",
                },
              }}
            >
              Limpar Filtros
            </Button>
          )}
        </Box>

        <Box sx={{ mt: 4 }}>
          <Typography variant="subtitle2" mb={2} fontWeight={600}>
            Início do evento
          </Typography>

          <Box sx={{ display: "flex", gap: 2 }}>
            <DatePicker
              label="De"
              value={tempFilters.startDate?.start || null}
              onChange={(date: Date | null) =>
                setTempFilters({
                  ...tempFilters,
                  startDate: {
                    ...tempFilters.startDate,
                    start: date,
                  },
                })
              }
              slotProps={{
                textField: { size: "small", fullWidth: true },
              }}
            />

            <DatePicker
              label="Até"
              value={tempFilters.startDate?.end || null}
              onChange={(date: Date | null) =>
                setTempFilters({
                  ...tempFilters,
                  startDate: {
                    ...tempFilters.startDate,
                    end: date,
                  },
                })
              }
              slotProps={{
                textField: { size: "small", fullWidth: true },
              }}
            />
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Box>
          <Typography variant="subtitle2" mb={2} fontWeight={600}>
            Finalização do evento
          </Typography>

          <Box sx={{ display: "flex", gap: 2 }}>
            <DatePicker
              label="De"
              value={tempFilters.endDate?.start || null}
              onChange={(date: Date | null) =>
                setTempFilters({
                  ...tempFilters,
                  endDate: {
                    ...tempFilters.endDate,
                    start: date,
                  },
                })
              }
              slotProps={{
                textField: { size: "small", fullWidth: true },
              }}
            />

            <DatePicker
              label="Até"
              value={tempFilters.endDate?.end || null}
              onChange={(date: Date | null) =>
                setTempFilters({
                  ...tempFilters,
                  endDate: {
                    ...tempFilters.endDate,
                    end: date,
                  },
                })
              }
              slotProps={{
                textField: { size: "small", fullWidth: true },
              }}
            />
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Box>
          <Typography variant="subtitle2" mb={2} fontWeight={600}>
            Faturamento
          </Typography>

          <Box sx={{ display: "flex", gap: 2 }}>
            <TextField
              label="Mínimo"
              type="number"
              size="small"
              fullWidth
              value={tempFilters.billingMin ?? ""}
              onChange={(e) => handleMinMaxChange("billingMin", e.target.value)}
              InputProps={{
                inputProps: { min: 0 },
              }}
            />

            <TextField
              label="Máximo"
              type="number"
              size="small"
              fullWidth
              value={tempFilters.billingMax ?? ""}
              onChange={(e) => handleMinMaxChange("billingMax", e.target.value)}
              InputProps={{
                inputProps: { min: 0 },
              }}
            />
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Box>
          <Typography variant="subtitle2" mb={2} fontWeight={600}>
            Receita
          </Typography>

          <Box sx={{ display: "flex", gap: 2 }}>
            <TextField
              label="Mínimo"
              type="number"
              size="small"
              fullWidth
              value={tempFilters.revenueMin ?? ""}
              onChange={(e) => handleMinMaxChange("revenueMin", e.target.value)}
              InputProps={{
                inputProps: { min: 0 },
              }}
            />

            <TextField
              label="Máximo"
              type="number"
              size="small"
              fullWidth
              value={tempFilters.revenueMax ?? ""}
              onChange={(e) => handleMinMaxChange("revenueMax", e.target.value)}
              InputProps={{
                inputProps: { min: 0 },
              }}
            />
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Box>
          <Typography variant="subtitle2" mb={2} fontWeight={600}>
            Público
          </Typography>

          <Box sx={{ display: "flex", gap: 2 }}>
            <TextField
              label="Mínimo"
              type="number"
              size="small"
              fullWidth
              value={tempFilters.audienceMin ?? ""}
              onChange={(e) => handleMinMaxChange("audienceMin", e.target.value)}
              InputProps={{
                inputProps: { min: 0 },
              }}
            />

            <TextField
              label="Máximo"
              type="number"
              size="small"
              fullWidth
              value={tempFilters.audienceMax ?? ""}
              onChange={(e) => handleMinMaxChange("audienceMax", e.target.value)}
              InputProps={{
                inputProps: { min: 0 },
              }}
            />
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        <FormControl fullWidth size="small">
          <InputLabel>Evento</InputLabel>
          <Select
            value={tempFilters.eventScope || "Todos"}
            label="Evento"
            onChange={(e) =>
              setTempFilters({
                ...tempFilters,
                eventScope:
                  e.target.value === "Todos" ? undefined : e.target.value,
              })
            }
          >
            <MenuItem value="Todos">Todos</MenuItem>
            <MenuItem value="Nacional">Nacional</MenuItem>
            <MenuItem value="Internacional">Internacional</MenuItem>
            <MenuItem value="Ambos">Ambos</MenuItem>
          </Select>
        </FormControl>

        <Box sx={{ mt: 2 }}>
          <FormControl fullWidth size="small">
            <InputLabel>Concorrência</InputLabel>
            <Select
              value={
                tempFilters.hasCompetitor === undefined
                  ? "Todos"
                  : tempFilters.hasCompetitor
                  ? "Sim"
                  : "Não"
              }
              label="Concorrência"
              onChange={(e) =>
                setTempFilters({
                  ...tempFilters,
                  hasCompetitor:
                    e.target.value === "Todos"
                      ? undefined
                      : e.target.value === "Sim",
                })
              }
            >
              <MenuItem value="Todos">Todos</MenuItem>
              <MenuItem value="Sim">Sim</MenuItem>
              <MenuItem value="Não">Não</MenuItem>
            </Select>
          </FormControl>
        </Box>

        <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
          <Button
            variant="contained"
            color="primary"
            fullWidth
            disabled={!hasChanges}
            onClick={handleApplyFilters}
            sx={{
              textTransform: 'none',
              py: 1.2
            }}
          >
            Aplicar Filtros
          </Button>
        </Box>
      </Box>
    </Box>
  );
};
